/**
 * ONE Intake — omnichannel inbox for requests coming from email / API / uploads.
 * Skeleton with tabs by state and manual submit form for testing.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Search, CheckCircle2, XCircle, ArrowRight } from "lucide-react";

const STATES = ["new", "review_required", "ready_to_process", "processed", "exception", "failed", "archived"];

export default function OneIntake() {
  const [state, setState] = useState("review_required");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => api.get("/intake", { params: { state } }).then(({ data }) => {
    setItems(data.items || []);
    setCounts(data.counts || {});
  }), [state]);
  useEffect(() => { load(); }, [state, load]);

  return (
    <div className="max-w-5xl space-y-6" data-testid="one-intake">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Automation</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">ONE Intake</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Every request — via portal, email, API or file drop — lands here. Each intake carries confidence, matched customer + employee,
          and any proposed changes are surfaced for human review before touching your verified records.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {STATES.map((s) => (
          <button key={s} onClick={() => setState(s)} data-testid={`intake-tab-${s}`}
            className={
              "px-3 h-9 text-sm rounded-t-md flex items-center gap-2 border-b-2 -mb-px transition-colors " +
              (state === s
                ? "border-slate-900 text-slate-900 font-medium bg-slate-50"
                : "border-transparent text-slate-500 hover:text-slate-900")
            }>
            {s.replace("_", " ")}
            {counts[s] ? <span className="text-[10px] tabular-nums rounded bg-slate-200 text-slate-700 px-1">{counts[s]}</span> : null}
          </button>
        ))}
        <div className="ml-auto flex items-center">
          <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800" data-testid="intake-new">
            Simulate email
          </button>
        </div>
      </div>

      {showForm && <IntakeForm onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Inbox className="h-4 w-4" /> Nothing here yet.</div>
        ) : items.map((it) => <IntakeRow key={it.id} it={it} onChanged={load} />)}
      </div>
    </div>
  );
}

function IntakeRow({ it, onChanged }) {
  const [expand, setExpand] = useState(false);
  const [checked, setChecked] = useState([]);
  const approve = async () => {
    await api.post(`/intake/${it.id}/approve`, { accept_fields: checked });
    toast.success("Processed");
    onChanged();
  };
  const reject = async () => {
    await api.post(`/intake/${it.id}/reject`, { reason: "Rejected in review" });
    toast.success("Rejected");
    onChanged();
  };
  const changes = it.proposed_changes || [];
  return (
    <div className="p-4" data-testid={`intake-row-${it.id}`}>
      <div className="flex items-start gap-3">
        <ConfBadge conf={it.confidence} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">{it.subject || "(no subject)"}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {it.channel} · {it.sender || "unknown sender"} · {it.received_at?.slice(0, 16).replace("T", " ")}
          </div>
          {it.extracted && Object.keys(it.extracted).length > 0 && (
            <div className="text-xs text-slate-700 mt-2">
              Extracted: <span className="text-slate-500">{Object.entries(it.extracted).map(([k, v]) => `${k}=${v}`).join(" · ")}</span>
            </div>
          )}
          {changes.length > 0 && (
            <div className="mt-2 border border-amber-200 bg-amber-50 rounded-md p-2 text-xs">
              <div className="font-semibold text-amber-800 mb-1">Proposed changes vs verified record:</div>
              <ul className="space-y-1">
                {changes.map((ch, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <input type="checkbox" checked={checked.includes(ch.field)}
                      onChange={(e) => setChecked(e.target.checked ? [...checked, ch.field] : checked.filter((x) => x !== ch.field))} />
                    <span className="font-medium text-slate-700">{ch.field}:</span>
                    <span className="line-through text-slate-500">{ch.current}</span>
                    <ArrowRight className="h-3 w-3 text-slate-400" />
                    <span className="text-slate-900">{ch.proposed}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <button onClick={() => setExpand(!expand)} className="text-xs text-slate-500 hover:text-slate-900 mt-2">{expand ? "Hide body" : "Show body"}</button>
          {expand && <pre className="mt-2 text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-100 rounded p-2">{it.body}</pre>}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {it.state !== "processed" && it.state !== "archived" && (
            <>
              <button onClick={approve} className="h-8 px-2 text-xs rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Approve</button>
              <button onClick={reject} className="h-8 px-2 text-xs rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> Reject</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfBadge({ conf }) {
  const styles = {
    high:             "text-emerald-700 bg-emerald-50 border-emerald-200",
    review_required:  "text-amber-700 bg-amber-50 border-amber-200",
    low:              "text-rose-700 bg-rose-50 border-rose-200",
    rejected:         "text-slate-500 bg-slate-50 border-slate-200",
  };
  return (
    <span className={"h-9 w-9 shrink-0 rounded-md text-[10px] font-semibold uppercase tracking-widest grid place-items-center border " + (styles[conf] || styles.low)}>
      {(conf || "?")[0]}
    </span>
  );
}

function IntakeForm({ onCancel, onCreated }) {
  const [f, setF] = useState({
    channel: "email",
    subject: "Laptop required for new employee",
    sender: "hr@abcglobal.com",
    sender_domain: "abcglobal.com",
    body: "Employee: Rahul Mehta\nEmployee ID: E1234\nCorporate Email: rahul@abcglobal.com\nPrimary Mobile: 9840012345\nSecondary Mobile: 9840099999\nAddress: 12 Some Street, Anna Nagar\nCity: Chennai\nDistrict: Chennai\nState: Tamil Nadu\nPIN: 600040\nJoining Date: 2026-08-10\nDevice: MacBook Air",
  });
  const [aiPreview, setAiPreview] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const submit = async () => {
    try {
      await api.post("/intake", f);
      toast.success("Intake created");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const aiParse = async (save) => {
    setAiBusy(true); setAiPreview(null);
    try {
      const { data } = await api.post("/intake/ai-parse", {
        body: f.body, sender: f.sender, sender_domain: f.sender_domain, subject: f.subject, save_as_draft: !!save,
      });
      setAiPreview(data);
      if (save) { toast.success("AI draft saved to inbox"); onCreated(); }
      else toast.success("Extracted with AI");
    } catch (e) { toast.error(e.response?.data?.detail || "AI parse failed"); }
    finally { setAiBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="intake-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Channel</div>
          <select className="input" value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })}>
            {["email", "portal", "api", "file_upload", "manual"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Sender</div>
          <input className="input" value={f.sender} onChange={(e) => setF({ ...f, sender: e.target.value, sender_domain: (e.target.value.split("@")[1] || "") })} />
        </label>
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Subject</div>
          <input className="input" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
        </label>
      </div>
      <label className="block">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Body</div>
        <textarea className="input min-h-[160px] font-mono text-xs" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
      </label>
      {aiPreview && (
        <div className="border border-indigo-200 bg-indigo-50/60 rounded-md p-3 text-xs" data-testid="intake-ai-preview">
          <div className="font-semibold text-indigo-900 mb-1">AI extraction · confidence: {aiPreview.confidence_estimate}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
            {Object.entries(aiPreview.extracted || {}).map(([k, v]) => (
              <div key={k}><span className="text-indigo-700 font-medium">{k}:</span> <span className="text-slate-800">{String(v)}</span></div>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-indigo-700 mt-2">{aiPreview.notice}</div>
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="h-9 px-3 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
        <button onClick={() => aiParse(false)} disabled={aiBusy}
          className="h-9 px-3 text-sm rounded-md border border-indigo-300 text-indigo-800 bg-white hover:bg-indigo-50 disabled:opacity-60"
          data-testid="intake-ai-parse">{aiBusy ? "Parsing…" : "Preview with AI"}</button>
        <button onClick={() => aiParse(true)} disabled={aiBusy}
          className="h-9 px-3 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
          data-testid="intake-ai-save">{aiBusy ? "Saving…" : "Save AI draft"}</button>
        <button onClick={submit} className="h-9 px-3 text-sm bg-slate-900 text-white hover:bg-slate-800 rounded-md" data-testid="intake-submit">Submit</button>
      </div>
    </div>
  );
}
